import React, { useEffect, useState } from "react";
import api, { fileUrl } from "../../api";
import { toast } from "sonner";
import { TextField } from "./Field";
import FileUpload from "./FileUpload";
import { Plus, Trash2, Save, ChevronUp, ChevronDown, Pencil, X, Youtube, Film, FolderPlus, ArrowRightLeft } from "lucide-react";

const uid = (p) => p + Math.random().toString(36).slice(2, 9);
const emptyCourse = () => ({ title: "", subtitle: "", level: "", duration: "", cover: "", description: "", modules: [] });
const emptyLesson = () => ({ id: uid("l-"), title: "", duration: "", description: "", videoType: "youtube", youtubeId: "", videoUrl: "", cover: "", photos: [] });
const emptyModule = () => ({ id: uid("m-"), title: "Новый модуль", lessons: [] });

function extractYT(input) {
  if (!input) return "";
  const m = input.trim().match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : input.trim();
}

export default function CoursesManager() {
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.listCourses().then(setCourses).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = async (id) => { const full = await api.getCourse(id); if (!full.modules) full.modules = []; setEditing(JSON.parse(JSON.stringify(full))); };
  const startNew = () => setEditing(emptyCourse());

  const save = async () => {
    if (!editing.title.trim()) return toast.error("Укажите название курса");
    const payload = {
      title: editing.title, subtitle: editing.subtitle, level: editing.level, duration: editing.duration,
      cover: editing.cover, description: editing.description,
      modules: (editing.modules || []).map((m) => ({ id: m.id, title: m.title, lessons: (m.lessons || []).map((l) => ({ ...l })) })),
    };
    try {
      if (editing.id) { payload.id = editing.id; await api.updateCourse(editing.id, payload); } else await api.createCourse(payload);
      toast.success("Курс сохранён"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ошибка сохранения"); }
  };

  const removeCourse = async (id) => { if (!window.confirm("Удалить курс?")) return; await api.deleteCourse(id); toast.success("Курс удалён"); load(); };

  const setC = (k, v) => setEditing((p) => ({ ...p, [k]: v }));
  const mutate = (fn) => setEditing((p) => { const c = JSON.parse(JSON.stringify(p)); fn(c); return c; });

  const addModule = () => mutate((c) => c.modules.push(emptyModule()));
  const setModuleTitle = (mi, v) => mutate((c) => { c.modules[mi].title = v; });
  const delModule = (mi) => mutate((c) => c.modules.splice(mi, 1));
  const moveModule = (mi, d) => mutate((c) => { const j = mi + d; if (j < 0 || j >= c.modules.length) return; [c.modules[mi], c.modules[j]] = [c.modules[j], c.modules[mi]]; });

  const addLesson = (mi) => mutate((c) => c.modules[mi].lessons.push(emptyLesson()));
  const setLesson = (mi, li, k, v) => mutate((c) => { c.modules[mi].lessons[li][k] = v; });
  const delLesson = (mi, li) => mutate((c) => c.modules[mi].lessons.splice(li, 1));
  const moveLesson = (mi, li, d) => mutate((c) => { const ls = c.modules[mi].lessons; const j = li + d; if (j < 0 || j >= ls.length) return; [ls[li], ls[j]] = [ls[j], ls[li]]; });
  const moveLessonToModule = (mi, li, targetMi) => mutate((c) => { const [l] = c.modules[mi].lessons.splice(li, 1); c.modules[targetMi].lessons.push(l); });
  const addPhoto = (mi, li, url) => mutate((c) => { c.modules[mi].lessons[li].photos = [...(c.modules[mi].lessons[li].photos || []), url]; });
  const delPhoto = (mi, li, pi) => mutate((c) => { c.modules[mi].lessons[li].photos.splice(pi, 1); });

  if (editing) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="flex items-center justify-between mb-5"><h2 className="text-xl font-bold">{editing.id ? "Редактирование курса" : "Новый курс"}</h2><button onClick={() => setEditing(null)} className="text-white/50 hover:text-white flex items-center gap-1 text-sm"><X size={16} /> Закрыть</button></div>

        <div className="grid md:grid-cols-2 gap-4">
          <TextField label="Название курса" value={editing.title} onChange={(v) => setC("title", v)} />
          <TextField label="Подзаголовок" value={editing.subtitle} onChange={(v) => setC("subtitle", v)} />
          <TextField label="Уровень" value={editing.level} onChange={(v) => setC("level", v)} placeholder="Для начинающих" />
          <TextField label="Длительность" value={editing.duration} onChange={(v) => setC("duration", v)} placeholder="6 часов" />
          <div className="md:col-span-2"><TextField label="Описание" textarea value={editing.description} onChange={(v) => setC("description", v)} /></div>
          <div className="md:col-span-2"><FileUpload label="Обложка курса (файл)" kind="image" value={editing.cover} onChange={(v) => setC("cover", v)} /></div>
        </div>

        <div className="mt-8 flex items-center justify-between"><h3 className="font-bold">Модули и уроки</h3><button onClick={addModule} className="flex items-center gap-1.5 rounded-full bg-[#d6ff71] text-black px-4 py-2 text-sm font-semibold hover:bg-[#c4f24d]"><FolderPlus size={16} /> Добавить модуль</button></div>

        <div className="mt-4 space-y-5">
          {(editing.modules || []).length === 0 && <p className="text-white/40 text-sm">Нет модулей. Добавьте первый блок.</p>}
          {(editing.modules || []).map((mod, mi) => (
            <div key={mod.id} className="rounded-2xl border border-[#ff3798]/20 bg-black/20 p-4">
              <div className="flex items-center gap-2 mb-3">
                <input value={mod.title} onChange={(e) => setModuleTitle(mi, e.target.value)} className="flex-1 rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm font-semibold outline-none focus:border-[#ff3798]" />
                <button onClick={() => moveModule(mi, -1)} className="p-1.5 text-white/40 hover:text-white"><ChevronUp size={16} /></button>
                <button onClick={() => moveModule(mi, 1)} className="p-1.5 text-white/40 hover:text-white"><ChevronDown size={16} /></button>
                <button onClick={() => delModule(mi)} className="p-1.5 text-white/40 hover:text-red-400"><Trash2 size={16} /></button>
              </div>

              <div className="space-y-3">
                {(mod.lessons || []).map((l, li) => (
                  <div key={l.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="h-7 w-7 shrink-0 rounded-full bg-[#ff3798]/20 text-[#ff3798] flex items-center justify-center text-xs font-bold">{li + 1}</span>
                      <div className="ml-auto flex items-center gap-1">
                        {editing.modules.length > 1 && (
                          <select value="" onChange={(e) => e.target.value !== "" && moveLessonToModule(mi, li, Number(e.target.value))} title="Перенести в модуль" className="rounded-lg bg-black/40 border border-white/10 text-xs px-2 py-1 outline-none focus:border-[#ff3798] mr-1">
                            <option value="">→ модуль</option>
                            {editing.modules.map((m2, idx) => idx !== mi && <option key={m2.id} value={idx}>{m2.title}</option>)}
                          </select>
                        )}
                        <button onClick={() => moveLesson(mi, li, -1)} className="p-1.5 text-white/40 hover:text-white"><ChevronUp size={15} /></button>
                        <button onClick={() => moveLesson(mi, li, 1)} className="p-1.5 text-white/40 hover:text-white"><ChevronDown size={15} /></button>
                        <button onClick={() => delLesson(mi, li)} className="p-1.5 text-white/40 hover:text-red-400"><Trash2 size={15} /></button>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <TextField label="Название урока" value={l.title} onChange={(v) => setLesson(mi, li, "title", v)} placeholder="Урок 1. Введение" />
                      <TextField label="Длительность" value={l.duration} onChange={(v) => setLesson(mi, li, "duration", v)} placeholder="12:30" />
                      <div className="md:col-span-2"><TextField label="Описание урока (текст)" textarea rows={2} value={l.description} onChange={(v) => setLesson(mi, li, "description", v)} /></div>
                    </div>

                    <div className="mt-3">
                      <span className="text-xs text-white/50 mb-1.5 block">Видео</span>
                      <div className="flex gap-2 mb-2">
                        <button onClick={() => setLesson(mi, li, "videoType", "youtube")} className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs border transition-colors ${l.videoType !== "file" ? "border-[#ff3798] bg-[#ff3798]/10 text-[#ff9ccb]" : "border-white/10 text-white/50"}`}><Youtube size={14} /> YouTube</button>
                        <button onClick={() => setLesson(mi, li, "videoType", "file")} className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs border transition-colors ${l.videoType === "file" ? "border-[#d6ff71] bg-[#d6ff71]/10 text-[#d6ff71]" : "border-white/10 text-white/50"}`}><Film size={14} /> Загрузить файл</button>
                      </div>
                      {l.videoType === "file" ? (
                        <FileUpload kind="video" value={l.videoUrl} onChange={(v) => setLesson(mi, li, "videoUrl", v)} />
                      ) : (
                        <TextField value={l.youtubeId} onChange={(v) => setLesson(mi, li, "youtubeId", extractYT(v))} placeholder="Ссылка YouTube или ID" />
                      )}
                    </div>

                    <div className="mt-3 grid md:grid-cols-2 gap-4">
                      <FileUpload label="Обложка урока" kind="image" compact value={l.cover} onChange={(v) => setLesson(mi, li, "cover", v)} />
                      <div>
                        <span className="text-xs text-white/50 mb-1.5 block">Фото под уроком</span>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {(l.photos || []).map((p, pi) => (
                            <div key={pi} className="relative"><img src={fileUrl(p)} alt="" className="h-14 w-14 rounded-lg object-cover border border-white/10" /><button onClick={() => delPhoto(mi, li, pi)} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-black border border-white/20 flex items-center justify-center text-white/70 hover:text-red-400"><X size={11} /></button></div>
                          ))}
                        </div>
                        <FileUpload kind="image" value="" onChange={(v) => addPhoto(mi, li, v)} />
                      </div>
                    </div>
                  </div>
                ))}
                <button onClick={() => addLesson(mi)} className="flex items-center gap-1.5 text-sm text-[#d6ff71]"><Plus size={15} /> Добавить урок</button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex gap-3 sticky bottom-4">
          <button onClick={save} className="flex items-center gap-2 rounded-full bg-[#ff3798] px-6 py-2.5 font-semibold hover:bg-[#ff5aac] transition-colors shadow-[0_10px_28px_-10px_rgba(255,55,152,0.9)]"><Save size={17} /> Сохранить курс</button>
          <button onClick={() => setEditing(null)} className="rounded-full border border-white/15 px-6 py-2.5 text-white/70 hover:border-white/30">Отмена</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5"><p className="text-white/50 text-sm">Всего курсов: {courses.length}</p><button onClick={startNew} className="flex items-center gap-1.5 rounded-full bg-[#ff3798] px-5 py-2.5 text-sm font-semibold hover:bg-[#ff5aac]"><Plus size={16} /> Новый курс</button></div>
      <div className="grid md:grid-cols-2 gap-4">
        {courses.map((c) => (
          <div key={c.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex gap-4">
            <img src={c.cover} alt={c.title} className="h-20 w-24 object-cover rounded-xl border border-white/10" />
            <div className="min-w-0 flex-1">
              <h3 className="font-bold truncate">{c.title}</h3>
              <p className="text-xs text-white/40 mt-0.5">{c.level} · {(c.modules || []).length} модулей · {(c.modules || []).reduce((n, m) => n + (m.lessons || []).length, 0)} уроков</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => openEdit(c.id)} className="flex items-center gap-1.5 rounded-full border border-[#d6ff71]/50 text-[#d6ff71] px-3 py-1.5 text-xs hover:bg-[#d6ff71] hover:text-black"><Pencil size={13} /> Редактировать</button>
                <button onClick={() => removeCourse(c.id)} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-white/60 hover:text-red-400 hover:border-red-400/40"><Trash2 size={13} /> Удалить</button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
